/**
 * StaffPermissionsPage — Gestão de Permissões de Acesso (Ferramentas)
 * Gestor controla quais módulos cada staff/atleta pode acessar.
 *
 * Endpoints:
 *   GET  /api/staff-permissions?club_id=
 *   PUT  /api/staff-permissions  { club_id, target_user_id, tools: [...] }
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Shield, Check, X, Save, RefreshCw, Eye, Edit3, Plus, Tag } from 'lucide-react';
import { VUKZ, VUKZ_GRADIENT } from '../../theme/vukzTheme';
import PageHeader from '../../components/layout/PageHeader';
import { AZURE } from '../../services/api';
import { useActiveContext } from '../../contexts/ActiveContextContext';
import { useRBAC } from '../../contexts/RBACContext';
import { useGovernanceLang } from '../../contexts/GovernanceLangContext';

const STAFF_LABELS = {
  pt: {
    roleMap: {
      TREINADOR: 'Treinador', ATLETA: 'Atleta',
      PREPARADOR_FISICO: 'Prep. Físico', AUX_PREP_FISICA: 'Aux. Prep.',
    },
    toggleView: 'Ver', toggleEdit: 'Editar',
    pageTitle: 'Permissões de Acesso',
    pageSubtitle: 'Controle quais funcionalidades cada membro pode acessar',
    features: 'Funcionalidades',
    linkedCats: 'Categorias Vinculadas',
    addCat: '+ Adicionar categoria',
    noCats: 'Sem categorias cadastradas — acesse Elenco para criar.',
    emptyTitle: 'Nenhum staff ou atleta encontrado',
    emptyBody: 'Cadastre membros no Elenco primeiro.',
    saving: 'Salvando...', save: 'Salvar',
  },
  en: {
    roleMap: {
      TREINADOR: 'Coach', ATLETA: 'Athlete',
      PREPARADOR_FISICO: 'S&C Coach', AUX_PREP_FISICA: 'Asst. S&C',
    },
    toggleView: 'View', toggleEdit: 'Edit',
    pageTitle: 'Access Permissions',
    pageSubtitle: 'Control which features each member can access',
    features: 'Features',
    linkedCats: 'Linked Categories',
    addCat: '+ Add category',
    noCats: 'No categories registered — go to Roster to create.',
    emptyTitle: 'No staff or athletes found',
    emptyBody: 'Register members in the Roster first.',
    saving: 'Saving...', save: 'Save',
  },
};

// ─── Role Badge ───────────────────────────────────────────────────────────────
const ROLE_COLORS = {
  TREINADOR: { bg: '#dbeafe', text: '#1e40af' },
  ATLETA: { bg: '#d1fae5', text: '#065f46' },
  PREPARADOR_FISICO: { bg: '#fef3c7', text: '#92400e' },
  AUX_PREP_FISICA: { bg: '#fce7f3', text: '#9d174d' },
};

const RoleBadge = ({ role, t }) => {
  const c = ROLE_COLORS[role] || { bg: '#f3f4f6', text: '#374151' };
  const label = t.roleMap[role] || role;
  return (
    <span className="px-2 py-0.5 rounded text-[10px] font-bold" style={{ backgroundColor: c.bg, color: c.text }}>
      {label}
    </span>
  );
};

// ─── Toggle ───────────────────────────────────────────────────────────────────
const PermToggle = ({ active, onClick, icon: Icon, label }) => (
  <button
    onClick={onClick}
    className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold border transition-all"
    style={{
      backgroundColor: active ? '#d1fae5' : '#fff',
      borderColor: active ? '#86efac' : '#e5e7eb',
      color: active ? '#065f46' : '#9ca3af',
    }}
  >
    <Icon size={10} />
    {label}
  </button>
);

// ─── Main Page ────────────────────────────────────────────────────────────────
const StaffPermissionsPage = () => {
  const { clubId: ctxClubId } = useActiveContext();
  const { clubId: rbacClubId } = useRBAC();
  const clubId = ctxClubId || rbacClubId;
  const { lang } = useGovernanceLang();
  const t = STAFF_LABELS[lang] || STAFF_LABELS.pt;
  const [users, setUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [dirty, setDirty] = useState({});

  const fetchData = useCallback(async () => {
    if (!clubId) return;
    setLoading(true);
    try {
      const [permRes, catRes, scopeRes] = await Promise.allSettled([
        AZURE.get('/staff-permissions', { params: { club_id: clubId } }),
        AZURE.get('/roster/categories', { params: { institution_id: clubId } }),
        AZURE.get('/rbac/staff-categories', { params: { club_id: clubId } }),
      ]);
      const usersData = permRes.status === 'fulfilled' ? (permRes.value.data?.data || []) : [];
      const catsData = catRes.status === 'fulfilled' ? (catRes.value.data?.data || []) : [];
      const scopesData = scopeRes.status === 'fulfilled' ? (scopeRes.value.data?.data || []) : [];

      // Merge category scopes into users
      const scopeMap = {};
      scopesData.forEach(s => {
        if (!scopeMap[s.user_id]) scopeMap[s.user_id] = [];
        scopeMap[s.user_id].push(s.category_name);
      });
      usersData.forEach(u => { u.categories = scopeMap[u.user_id] || []; });

      setUsers(usersData);
      setCategories(catsData);
      setDirty({});
    } catch (err) {
      console.error('Error loading permissions:', err);
    } finally {
      setLoading(false);
    }
  }, [clubId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleTool = (userIdx, toolIdx, field) => {
    setUsers(prev => {
      const next = [...prev];
      const user = { ...next[userIdx] };
      const tools = [...user.tools];
      tools[toolIdx] = { ...tools[toolIdx], [field]: !tools[toolIdx][field] };
      // if can_write is on, can_read must be on too
      if (field === 'can_write' && tools[toolIdx].can_write) {
        tools[toolIdx].can_read = true;
      }
      // if can_read is off, can_write must be off
      if (field === 'can_read' && !tools[toolIdx].can_read) {
        tools[toolIdx].can_write = false;
      }
      user.tools = tools;
      next[userIdx] = user;
      return next;
    });
    setDirty(prev => ({ ...prev, [users[userIdx].user_id]: true }));
  };

  const saveUser = async (user) => {
    setSaving(user.user_id);
    try {
      await AZURE.put('/staff-permissions', {
        club_id: clubId,
        target_user_id: user.user_id,
        tools: user.tools.map(t => ({ tool_key: t.tool_key, can_read: t.can_read, can_write: t.can_write })),
      });
      setDirty(prev => { const n = { ...prev }; delete n[user.user_id]; return n; });
    } catch (err) {
      console.error('Error saving permissions:', err);
    } finally {
      setSaving(null);
    }
  };

  const assignCategory = async (userId, categoryName) => {
    try {
      await AZURE.post('/rbac/staff-categories/assign', {
        staff_user_id: userId, club_id: clubId, sport: 'natacao', category_name: categoryName,
      });
      fetchData();
    } catch (err) { console.error('Error assigning category:', err); }
  };

  const removeCategory = async (userId, categoryName) => {
    try {
      await AZURE.post('/rbac/staff-categories/remove', {
        staff_user_id: userId, club_id: clubId, sport: 'natacao', category_name: categoryName,
      });
      fetchData();
    } catch (err) { console.error('Error removing category:', err); }
  };

  if (loading) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: VUKZ.bg }}>
        <div className="max-w-5xl mx-auto px-4 lg:px-6 py-6 space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-32 rounded-xl animate-pulse" style={{ backgroundColor: VUKZ.primaryLight }} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: VUKZ.bg }}>
      <div className="max-w-5xl mx-auto px-4 lg:px-6 py-6">

        {/* Header */}
        <div className="bg-white rounded-2xl border overflow-hidden mb-6" style={{ borderColor: VUKZ.border }}>
          <div className="px-6 py-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: VUKZ.primaryLight }}>
                  <Shield size={18} style={{ color: VUKZ.primary }} />
                </div>
                <div>
                  <h1 className="text-lg font-extrabold" style={{ color: VUKZ.text }}>{t.pageTitle}</h1>
                  <p className="text-[10px]" style={{ color: VUKZ.textLight }}>
                    {t.pageSubtitle}
                  </p>
                </div>
              </div>
              <button
                onClick={fetchData}
                className="p-2 rounded-lg border hover:bg-gray-50 transition-all"
                style={{ borderColor: VUKZ.border }}
              >
                <RefreshCw size={14} style={{ color: VUKZ.textLight }} />
              </button>
            </div>
          </div>
        </div>

        {/* Users list */}
        {users.length === 0 ? (
          <div className="bg-white rounded-xl border p-8 text-center" style={{ borderColor: VUKZ.border }}>
            <Shield size={32} className="mx-auto mb-3" style={{ color: VUKZ.border }} />
            <p className="text-sm font-bold" style={{ color: VUKZ.text }}>{t.emptyTitle}</p>
            <p className="text-xs" style={{ color: VUKZ.textLight }}>{t.emptyBody}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {users.map((user, userIdx) => (
              <div
                key={user.user_id}
                className="bg-white rounded-xl border overflow-hidden"
                style={{ borderColor: dirty[user.user_id] ? VUKZ.primary : VUKZ.border }}
              >
                {/* User header */}
                <div className="px-5 py-3 flex items-center justify-between border-b" style={{ borderColor: VUKZ.border }}>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-black text-white"
                      style={{ background: VUKZ_GRADIENT }}>
                      {user.full_name?.charAt(0) || '?'}
                    </div>
                    <div>
                      <span className="text-sm font-bold" style={{ color: VUKZ.text }}>{user.full_name}</span>
                      {user.email && (
                        <span className="text-[10px] ml-2" style={{ color: VUKZ.textLight }}>{user.email}</span>
                      )}
                    </div>
                    <RoleBadge role={user.role_key} t={t} />
                  </div>

                  {dirty[user.user_id] && (
                    <button
                      onClick={() => saveUser(user)}
                      disabled={saving === user.user_id}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-white transition-all hover:opacity-90 disabled:opacity-50"
                      style={{ background: VUKZ_GRADIENT }}
                    >
                      <Save size={12} />
                      {saving === user.user_id ? t.saving : t.save}
                    </button>
                  )}
                </div>

                {/* Tools grid */}
                <div className="px-5 py-3">
                  <div className="text-[10px] font-bold uppercase tracking-wide mb-2" style={{ color: VUKZ.textLight }}>
                    {t.features}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {user.tools.map((tool, toolIdx) => (
                      <div
                        key={tool.tool_key}
                        className="flex items-center justify-between p-2.5 rounded-lg border"
                        style={{
                          borderColor: (tool.can_read || tool.can_write) ? '#d1fae5' : '#f3f4f6',
                          backgroundColor: (tool.can_read || tool.can_write) ? '#f0fdf4' : '#fafafa',
                        }}
                      >
                        <div className="min-w-0">
                          <div className="text-xs font-bold truncate" style={{ color: VUKZ.text }}>
                            {tool.label}
                          </div>
                          <div className="text-[9px] truncate" style={{ color: VUKZ.textLight }}>
                            {tool.description}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                          <PermToggle
                            active={tool.can_read}
                            onClick={() => toggleTool(userIdx, toolIdx, 'can_read')}
                            icon={Eye}
                            label={t.toggleView}
                          />
                          <PermToggle
                            active={tool.can_write}
                            onClick={() => toggleTool(userIdx, toolIdx, 'can_write')}
                            icon={Edit3}
                            label={t.toggleEdit}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Categories section (only for staff roles) */}
                {['TREINADOR', 'PREPARADOR_FISICO', 'AUX_PREP_FISICA'].includes(user.role_key) && (
                  <div className="px-5 py-3 border-t" style={{ borderColor: VUKZ.border }}>
                    <div className="text-[10px] font-bold uppercase tracking-wide mb-2" style={{ color: VUKZ.textLight }}>
                      {t.linkedCats}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {(user.categories || []).map(cat => (
                        <span key={cat} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold border"
                          style={{ backgroundColor: '#f0fdf4', borderColor: '#86efac', color: '#065f46' }}>
                          <Tag size={9} /> {cat}
                          <button onClick={() => removeCategory(user.user_id, cat)} className="ml-0.5 hover:text-red-600">
                            <X size={10} />
                          </button>
                        </span>
                      ))}
                      {categories.filter(c => !(user.categories || []).includes(c.name)).length > 0 && (
                        <select
                          className="text-[10px] px-2 py-1 rounded-lg border font-bold"
                          style={{ borderColor: VUKZ.border, color: VUKZ.primary }}
                          value=""
                          onChange={(e) => { if (e.target.value) assignCategory(user.user_id, e.target.value); }}
                        >
                          <option value="">{t.addCat}</option>
                          {categories.filter(c => !(user.categories || []).includes(c.name)).map(c => (
                            <option key={c.id || c.name} value={c.name}>{c.name}</option>
                          ))}
                        </select>
                      )}
                      {(user.categories || []).length === 0 && categories.length === 0 && (
                        <span className="text-[10px]" style={{ color: VUKZ.textLight }}>
                          {t.noCats}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default StaffPermissionsPage;
